import { CashRegisterItem, Name } from '../interface/response/CashRegisterDataResponse';
import { fetchCashRegisterDetails, resetCashRegisterDetails } from '../store/slice/cash-register-details-slice';
import { fetchOperatorDetails, resetOperatorDetails } from '../store/slice/operator-details-slice';
import { useAllFilters } from './useAllFilters';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import useDateAndTime from './useDateAndTime';

/**
 * @function getCashRegisterDetails
 * @param {CashRegisterItem} item - The cash register item containing necessary identifiers or data for the fetch.
 *
 * Dispatches an action to fetch details of a cash register.
 *
 * @remarks
 * Assumes that `fetchCashRegisterDetails` is a Redux async action (thunk) that updates the store.
 *
 * @function getOperatorDetails
 * @param {Name} item - The operator data object containing identifiers such as name or ID.
 *
 * Dispatches an action to fetch details of an operator.
 *
 * @remarks
 * - Returns early if no `selectedItem` is set.
 * - Assumes that `fetchOperatorDetails` is a Redux async action (thunk) that updates the store.
 */

export const useFetchDetails = () => {
  const dispatch = useAppDispatch();

  const selectedItem = useAppSelector((state) => state.cashRegisterData.selectedItem);
  const { startDate, endDate } = useDateAndTime();
  const { handleApplyAllFilters } = useAllFilters();

  const getCashRegisterDetails = (item: CashRegisterItem) => {
    handleApplyAllFilters();
    dispatch(resetCashRegisterDetails());
    dispatch(
      fetchCashRegisterDetails({
        LocationID: item.LocationID,
        DeviceID: item.DeviceID,
        From: startDate,
        To: endDate,
      }),
    );
  };

  const getOperatorDetails = (item: Name) => {
    if (!selectedItem) return;
    handleApplyAllFilters();
    dispatch(resetOperatorDetails());
    dispatch(
      fetchOperatorDetails({
        LocationID: selectedItem.LocationID,
        DeviceID: selectedItem.DeviceID,
        UserName: item.UserName,
        From: startDate,
        To: endDate,
      }),
    );
  };

  return { getCashRegisterDetails, getOperatorDetails };
};
